// flash_message/createFlashMessageModal.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createFlashMessage } from "../../redux/actions/flashMessageActions";
import theme from "../../components/shared/theme";
import { FiX } from "react-icons/fi";
import { format } from "date-fns";
import toast from "react-hot-toast";

const CreateFlashMessageModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
    is_active: 1,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Message is required");
      return;
    }
    if (!formData.start_date) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.end_date) {
      toast.error("End date is required");
      return;
    }
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error("End date cannot be before start date");
      return;
    }

    setLoading(true);
    try {
      await dispatch(createFlashMessage(formData));
      toast.success("Flash message created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.message || "Failed to create flash message");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: theme.spacing.lg,
        }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Create Flash Message
          </h2>
          <button
            onClick={onClose}
            style={iconButtonStyle(theme.colors.gray)}
            disabled={loading}
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={labelStyle}>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter flash message title"
              style={inputStyle}
              disabled={loading}
              required
            />
          </div>

          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={labelStyle}>Message *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Enter flash message content"
              rows="4"
              style={{ ...inputStyle, resize: "vertical" }}
              disabled={loading}
              required
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing.md, marginBottom: theme.spacing.md }}>
            <div>
              <label style={labelStyle}>Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                style={inputStyle}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>End Date *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                style={inputStyle}
                disabled={loading}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: theme.spacing.lg }}>
            <label style={labelStyle}>Status</label>
            <div style={{ display: "flex", gap: theme.spacing.md, marginTop: theme.spacing.xs }}>
              <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input
                  type="radio"
                  name="is_active"
                  value="1"
                  checked={formData.is_active === 1}
                  onChange={() => setFormData(prev => ({ ...prev, is_active: 1 }))}
                  disabled={loading}
                />
                Active
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input
                  type="radio"
                  name="is_active"
                  value="0"
                  checked={formData.is_active === 0}
                  onChange={() => setFormData(prev => ({ ...prev, is_active: 0 }))}
                  disabled={loading}
                />
                Inactive
              </label>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: theme.spacing.sm }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                ...buttonStyle(theme.colors.gray),
                backgroundColor: "transparent",
                color: theme.colors.grayDark,
                border: `1px solid ${theme.colors.gray}`,
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...buttonStyle(theme.colors.success),
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: "#fff",
  borderRadius: theme.radius.xl,
  padding: theme.spacing.lg,
  boxShadow: theme.shadow.lg,
  width: "500px",
  maxWidth: "95%",
  maxHeight: "90vh",
  overflowY: "auto",
};

const labelStyle = {
  display: "block",
  fontSize: "0.85rem",
  fontWeight: 500,
  marginBottom: "4px",
  color: theme.colors.grayDark,
};

const inputStyle = {
  width: "100%",
  padding: theme.spacing.sm,
  border: `1px solid ${theme.colors.grayLight}`,
  borderRadius: theme.radius.md,
  fontSize: "0.9rem",
  fontFamily: "inherit",
};

const buttonStyle = (bgColor) => ({
  backgroundColor: bgColor,
  color: "#fff",
  border: "none",
  borderRadius: theme.radius.md,
  padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
  fontSize: "0.9rem",
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing.xs,
});

const iconButtonStyle = (color) => ({
  backgroundColor: "transparent",
  color: color,
  border: "none",
  borderRadius: theme.radius.sm,
  padding: "6px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
});

export default CreateFlashMessageModal;